import React, { useState } from "react";
import s from "../../style/home.module.less";

/* Flat, deterministic color per card — cycling the same four-hue palette
   used for token badges (TokenLogo), instead of a per-card gradient. */
const SWATCH = ["yellow", "blue", "violet", "green"];

/* ---------- how it works ---------- */
const STEP_SWATCH = ["yellow", "blue", "violet"];

export const HowItWorks = () => (
  <section className={s.section} id="how">
    <span className="ds-eyebrow">How it works</span>
    <h2 className={s.sectionTitle}>Connect, pick a tool, sign</h2>
    <div className={s.steps}>
      {[
        {
          n: "01",
          t: "Connect your wallet",
          d: "Any Solana wallet. No account, no email.",
        },
        {
          n: "02",
          t: "Pick a tool",
          d: "Create, airdrop, snapshot, reclaim. Devnet or mainnet.",
        },
        {
          n: "03",
          t: "Review and sign",
          d: "Solstack builds the transaction. You sign it in your wallet.",
        },
      ].map((x, i) => (
        <div key={x.n} className={s.step}>
          <span
            className={s.stepNum}
            style={{ background: `var(--v-${STEP_SWATCH[i]})` }}
          >
            {x.n}
          </span>
          <h3 className={s.stepTitle}>{x.t}</h3>
          <p className={s.stepText}>{x.d}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- why ---------- */
const fIcon = (paths) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {paths}
  </svg>
);

export const WhySolstack = () => (
  <section className={s.section} id="why">
    <span className="ds-eyebrow">Why Solstack</span>
    <h2 className={s.sectionTitle}>Why Solstack</h2>
    <div className={s.features}>
      {[
        { icon: fIcon(<><rect x="4.5" y="11" width="15" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>), t: "Your keys stay yours", d: "Every action is a transaction you sign in your wallet. Only Multisender and Airdrop hand the batches to our server, and the bots trade from their own wallet, each funded by your one approval. The EVM Dust Cleaner signs with the EVM keys you paste into its page, and they never leave that tab." },
        { icon: fIcon(<><circle cx="12" cy="12" r="9" /><path d="M15 9.5a3 3 0 0 0-3-2c-2 0-3 1-3 2.5s1.5 2 3 2.5 3 1 3 2.5-1 2.5-3 2.5a3 3 0 0 1-3-2M12 6v1.5M12 16.5V18" /></>), t: "Fees shown before you sign", d: "Read-only tools are free. Creating, distributing and trading carry a fee, shown as its own line." },
        { icon: fIcon(<><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>), t: "SPL + Token-2022", d: "Both standards, including transfer fees, interest and on-mint metadata." },
        { icon: fIcon(<><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>), t: "Batched and fast", d: "Multisends and airdrops run in batches with live progress." },
        { icon: fIcon(<><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 13h6M9 17h4" /></>), t: "CSV in and out", d: "Import recipient lists. Export snapshots." },
        { icon: fIcon(<><circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" /></>), t: "Devnet and mainnet", d: "One toggle. Rehearse on devnet, then flip." },
      ].map((x, i) => (
        <div key={x.t} className={s.feature}>
          <span
            className={s.featureIcon}
            style={{ background: `var(--v-${SWATCH[i % SWATCH.length]})` }}
          >
            {x.icon}
          </span>
          <h3 className={s.featureTitle}>{x.t}</h3>
          <p className={s.featureText}>{x.d}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const FAQS = [
  { q: "Do I need to sign up?", a: "No. Connect a wallet." },
  { q: "Is it custodial?", a: "Your keys never leave your wallet, and every transaction from your wallet is signed there. Three exceptions: Multisender and Airdrop, where your single approval funds a key made for that send, which Solstack's server holds until the send is done (tokens stay in your wallet under an approval for exactly the total); the grid and liquidity bots, which trade from a wallet of their own whose key the server holds until you stop the bot; and the EVM Dust Cleaner, where you paste the seed phrases or private keys of EVM wallets into its page. They stay in that browser tab, which signs the sales, and are never sent to Solstack." },
  { q: "What does it cost?", a: "Read-only tools are free. Token creation 0.1 SOL, launches 0.05 SOL, multisend and airdrop 0.001 SOL per recipient (less past 1,000, down to 0.00035), adding liquidity 0.02 SOL, a bot 0.1 SOL to start plus 0.5% of its swaps, a full holder CSV from 0.05 SOL, locks 0.02 SOL (devnet only for now). Rent Reclaim is free; Wallet Cleaner keeps 2% of the rent and of sales, and the EVM Dust Cleaner 2% of what each wallet bridges to SOL. Plus network fees, rent and storage." },
  { q: "Which wallets work?", a: "Any Solana wallet-standard wallet: Phantom, Solflare, Backpack, Ledger." },
  { q: "SPL or Token-2022?", a: "SPL for most tokens. Token-2022 for a transfer tax, interest or on-mint metadata." },
  { q: "Do you store my data?", a: "Never your keys, and no cookies or third-party tracking. Solstack keeps its own visit log, the address of a wallet you connect, the steps you reach in a tool, and what a send, a bot or a snapshot you save needs to run. The privacy policy lists all of it." },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className={s.section} id="faq">
      <span className="ds-eyebrow">FAQ</span>
      <h2 className={s.sectionTitle}>FAQ</h2>
      <div className={s.faq}>
        {FAQS.map((f, i) => (
          <div
            key={f.q}
            className={`${s.faqItem} ${open === i ? s.faqOpen : ""}`}
          >
            <button className={s.faqQ} onClick={() => setOpen(open === i ? -1 : i)} type="button">
              {f.q}
              <span className={s.faqChevron}>{open === i ? "–" : "+"}</span>
            </button>
            {open === i && <p className={s.faqA}>{f.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- final CTA ---------- */
const WALLETS = ["Phantom", "Solflare", "Backpack", "Ledger"];

export const FinalCta = ({ onOpen }) => (
  <section className={s.section}>
    <div className={`${s.cta} nb-cta-band`}>
      <div className="cta-banner">
        <div>
          <span className="chip cta-banner-chip">
            Free · your keys stay in your wallet · no signup
          </span>
          <h2 className="cta-banner-headline">
            Connect a wallet.
            <br />
            Ship a token
            <br />
            in a minute.
          </h2>
          <p className="cta-banner-copy">Free on devnet.</p>
          <div className="cta-banner-actions">
            <button className="primary-button" onClick={onOpen}>
              Enter Solstack →
            </button>
            <a className="primary-button ds-btn--secondary" href="/docs/overview">
              Read the docs
            </a>
          </div>
          <div className="cta-banner-wallets">
            {WALLETS.map((w) => (
              <span key={w} className="cta-wallet-chip">
                {w}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  </section>
);
